import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import {
  getPEUSITCTrade,
  getCachedPPUSITCTrade,
  getCachedPSUSITCTrade,
  getCachedPVCUSITCTrade,
  getCachedPETUSITCTrade,
} from '@/services/marketIntelService';
import { withAlpha } from '@/utils/chartColors';
import { useRsColors } from '@/hooks/useRsColors';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { sliceLabelsAndDatasets } from '@/utils/chartWindow';
import UsitcWorldMap from '@/components/polymer/UsitcWorldMap';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import { CheckboxDropdown } from '@/components/common/CheckboxDropdown';
import { useTheme } from '@/contexts/ThemeContext';
import { ChevronLeft, ChevronRight } from 'lucide-react';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip, Legend);

const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

type TimePeriod = 'current' | '1y' | '2y' | '5y';
type Unit = 'kgs' | 'lbs';

interface UsitcRecord {
  country: string;
  region: string;
  date: string;
  volume_kg: number;
  product_name: string;
  trade_direction: string;
}

interface UsitcChartProps {
  polymer: string;
}
const TIME_PERIOD_LABELS: Record<TimePeriod, string> = {
  current: 'Current Year',
  '1y': 'Last 1 Year',
  '2y': 'Last 2 Years',
  '5y': 'Last 5 Years',
};

const UsitcChart: React.FC<UsitcChartProps> = ({ polymer }) => {
  const { isDark } = useTheme();
  const [allData, setAllData] = useState<UsitcRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const hasFetched = useRef(false);

  const [selectedDirections, setSelectedDirections] = useState<Set<string>>(new Set(['Exports']));
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(new Set());
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(new Set());
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(new Set());
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('1y');
  const [unit, setUnit] = useState<Unit>('kgs');
  const [activeView, setActiveView] = useState<'chart' | 'map'>('chart');
  // Below 900px, the controls stack into 3 rows and the time period becomes a chevron selector.
  const [isCompact, setIsCompact] = useState(false);
  // For PE only: below 550px, exports/product/region/country display in 2x2 grid
  const [isPE2x2, setIsPE2x2] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 899px)');
    const pe2x2Mq = window.matchMedia('(max-width: 550px)');
    setIsCompact(mq.matches);
    setIsPE2x2(pe2x2Mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsCompact(e.matches);
    const pe2x2Handler = (e: MediaQueryListEvent) => setIsPE2x2(e.matches);
    mq.addEventListener('change', handler);
    pe2x2Mq.addEventListener('change', pe2x2Handler);
    return () => {
      mq.removeEventListener('change', handler);
      pe2x2Mq.removeEventListener('change', pe2x2Handler);
    };
  }, []);

  const polymerUpper = polymer.toUpperCase();
  const isPE = polymerUpper === 'PE';

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const load = async () => {
      setLoading(true);
      try {
        let data: UsitcRecord[] = [];
        if (isPE) {
          const [imports, exports] = await Promise.all([
            getPEUSITCTrade('import'),
            getPEUSITCTrade('export'),
          ]);
          data = [...imports, ...exports];
        } else if (polymerUpper === 'PP') {
          data = await getCachedPPUSITCTrade();
        } else if (polymerUpper === 'PS') {
          data = await getCachedPSUSITCTrade();
        } else if (polymerUpper === 'PVC') {
          data = await getCachedPVCUSITCTrade();
        } else if (polymerUpper === 'PET') {
          data = await getCachedPETUSITCTrade();
        }
        setAllData(Array.isArray(data) ? data : []);
      } catch {
        setAllData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const allProducts = useMemo(() => Array.from(new Set(allData.map(r => r.product_name).filter(Boolean))).sort(), [allData]);
  const allRegions = useMemo(() => Array.from(new Set(allData.map(r => r.region).filter(Boolean))).sort(), [allData]);
  const allCountries = useMemo(() => Array.from(new Set(allData.map(r => r.country).filter(Boolean))).sort(), [allData]);

  useEffect(() => { setSelectedProducts(new Set(allProducts)); }, [allProducts.join(',')]);
  useEffect(() => { setSelectedRegions(new Set(allRegions)); }, [allRegions.join(',')]);
  useEffect(() => { setSelectedCountries(new Set(allCountries)); }, [allCountries.join(',')]);

  const handleRegionChange = useCallback((newRegions: Set<string>) => {
    setSelectedRegions(newRegions);
    const derived = new Set(
      allData.filter(r => newRegions.has(r.region)).map(r => r.country).filter(Boolean)
    );
    setSelectedCountries(derived);
  }, [allData]);

  const handleCountryChange = useCallback((newCountries: Set<string>) => {
    setSelectedCountries(newCountries);
    const derived = new Set(
      allData.filter(r => newCountries.has(r.country)).map(r => r.region).filter(Boolean)
    );
    setSelectedRegions(derived);
  }, [allData]);

  const startDate = useMemo(() => {
    const now = new Date();
    if (timePeriod === 'current') return new Date(now.getFullYear(), 0, 1);
    const d = new Date(now);
    if (timePeriod === '1y') d.setMonth(d.getMonth() - 12);
    else if (timePeriod === '2y') d.setMonth(d.getMonth() - 24);
    else d.setMonth(d.getMonth() - 60);
    return d;
  }, [timePeriod]);

  const filtered = useMemo(() => {
    const startStr = startDate.toISOString().slice(0, 10);
    return allData.filter(r => {
      if (r.date < startStr) return false;
      if (selectedDirections.size > 0 && !selectedDirections.has('All')) {
        const matches = Array.from(selectedDirections).some(dir => {
          const dirMap = dir === 'Imports' ? 'import' : dir === 'Exports' ? 'export' : null;
          return dirMap === null || r.trade_direction === dirMap;
        });
        if (!matches) return false;
      }
      if (isPE && selectedProducts.size > 0 && !selectedProducts.has(r.product_name)) return false;
      if (selectedRegions.size > 0 && !selectedRegions.has(r.region)) return false;
      if (selectedCountries.size > 0 && !selectedCountries.has(r.country)) return false;
      return true;
    });
  }, [allData, startDate, selectedDirections, isPE, selectedProducts, selectedRegions, selectedCountries]);

  const { chartLabels, chartValues } = useMemo(() => {
    const map = new Map<string, number>();
    for (const r of filtered) {
      const bucket = r.date.slice(0, 7);
      const vol = unit === 'lbs' ? (Number(r.volume_kg) * 2.20462) : Number(r.volume_kg);
      map.set(bucket, (map.get(bucket) || 0) + vol);
    }
    const sorted = Array.from(map.entries()).sort((a, b) => a[0] < b[0] ? -1 : 1);
    const labels = sorted.map(([k]) => {
      const [year, mon] = k.split('-').map(Number);
      return `${MONTHS_SHORT[mon - 1]} ${year}`;
    });
    return { chartLabels: labels, chartValues: sorted.map(([, v]) => v) };
  }, [filtered, timePeriod, unit]);

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(chartLabels.length);

  const visibleLabels = useMemo(
    () => sliceLabelsAndDatasets(chartLabels, [{ data: chartValues, label: '' }], startIndex, endIndex).labels,
    [chartLabels, chartValues, startIndex, endIndex],
  );
  const visibleValues = useMemo(
    () => sliceLabelsAndDatasets(chartLabels, [{ data: chartValues, label: '' }], startIndex, endIndex).datasets[0].data as number[],
    [chartLabels, chartValues, startIndex, endIndex],
  );

  const yAxisCallback = useCallback((value: number | string) => {
    const v = Number(value);
    if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}bn`;
    if (v >= 1_000_000) return `${Math.round(v / 1_000_000)}M`;
    return String(v);
  }, []);

  const rs = useRsColors();

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);

  const chartTitle = useMemo(() => {
    const dirs = Array.from(selectedDirections).join('/') || 'Trade';
    return `${dirs} (${unit})`;
  }, [selectedDirections, unit]);

  const chartData = useMemo(() => ({
    labels: visibleLabels,
    datasets: [{
      label: chartTitle,
      data: visibleValues,
      borderColor: '#80ed40',
      backgroundColor: withAlpha('#80ed40', 0.7),
      fill: true,
      tension: 0.3,
      pointRadius: 0,
      pointHoverRadius: 4,
      borderWidth: 1.5,
      spanGaps: true,
    }],
  }), [visibleLabels, visibleValues, chartTitle, rs]);

  const unitLabel = unit;

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, right: 12, bottom: 8, left: 8 },
    },
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        mode: 'index' as const,
        intersect: false,
        callbacks: {
          label: (context: any) => `${unitLabel}  ${Number(context.raw).toLocaleString()}`,
        },
      },
      datalabels: { display: false },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: 11 },
          maxRotation: 0,
          autoSkip: false,
          callback: (_value: any, index: number, _ticks: any[]) => {
            const label = visibleLabels[index];
            if (!label) return '';
            if (timePeriod === '5y') {
              return label.startsWith('Jan') ? label.slice(4) : '';
            }
            if (timePeriod === '2y') {
              const monthAbbr = label.slice(0, 3);
              return ['Jan', 'Apr', 'Jul', 'Oct'].includes(monthAbbr) ? label : '';
            }
            return label;
          },
        },
        grid: { color: isDark ? 'rgba(243,244,246,0.15)' : 'rgba(100,100,100,0.3)' },
      },
      y: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: 11 },
          callback: yAxisCallback,
        },
        grid: { color: isDark ? 'rgba(243,244,246,0.15)' : 'rgba(100,100,100,0.3)' },
      },
    },
  }), [rs, unitLabel, yAxisCallback, timePeriod, visibleLabels, isDark]);

  return (
    <div className={`flex flex-col gap-3 h-full ${isCompact ? '-mx-6' : ''}`}>
      <div className={`${isPE && isPE2x2 ? 'grid grid-cols-2 gap-2 w-fit mx-auto' : `flex items-center gap-3 flex-wrap ${isCompact ? 'justify-center' : ''}`}`}>
        <CheckboxDropdown
          label="Exports"
          options={['Imports', 'Exports']}
          selected={selectedDirections}
          onChange={setSelectedDirections}
          singleSelect={true}
          hideCount={true}
          shiftMenuRight={true}
        />

        {isPE && (
          <CheckboxDropdown
            label="Product"
            options={allProducts}
            selected={selectedProducts}
            onChange={setSelectedProducts}
          />
        )}

        <CheckboxDropdown
          label="Region"
          options={allRegions}
          selected={selectedRegions}
          onChange={handleRegionChange}
        />

        <CheckboxDropdown
          label="Country"
          options={allCountries}
          selected={selectedCountries}
          onChange={handleCountryChange}
        />
      </div>

      {isCompact ? (
        <>
          {/* Middle row: time period as a chevron selector (1 at a time) */}
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => {
                const keys = Object.keys(TIME_PERIOD_LABELS) as TimePeriod[];
                const idx = keys.indexOf(timePeriod);
                setTimePeriod(keys[Math.max(0, idx - 1)]);
              }}
              disabled={(Object.keys(TIME_PERIOD_LABELS) as TimePeriod[]).indexOf(timePeriod) === 0}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
              aria-label="Previous time period"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </button>

            <button className="px-3 py-1.5 rounded text-sm font-medium bg-rs-green text-black whitespace-nowrap flex-shrink-0">
              {TIME_PERIOD_LABELS[timePeriod]}
            </button>

            <button
              onClick={() => {
                const keys = Object.keys(TIME_PERIOD_LABELS) as TimePeriod[];
                const idx = keys.indexOf(timePeriod);
                setTimePeriod(keys[Math.min(keys.length - 1, idx + 1)]);
              }}
              disabled={(Object.keys(TIME_PERIOD_LABELS) as TimePeriod[]).indexOf(timePeriod) === Object.keys(TIME_PERIOD_LABELS).length - 1}
              className={`p-1 rounded transition-colors ${isDark ? 'text-rs-green' : 'text-black'} hover:bg-rs-jet disabled:opacity-40`}
              aria-label="Next time period"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </button>
          </div>

          {/* Bottom row: kgs, lbs, chart, map */}
          <div className="flex items-center justify-center gap-2 flex-wrap">
            {(['kgs', 'lbs'] as Unit[]).map(u => (
              <button
                key={u}
                onClick={() => setUnit(u)}
                className={`px-3 py-1.5 rounded text-sm transition-colors ${
                  unit === u
                    ? 'bg-rs-green text-black font-medium'
                    : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                }`}
              >
                {u}
              </button>
            ))}

            {(['chart', 'map'] as const).map(v => (
              <button
                key={v}
                onClick={() => setActiveView(v)}
                className={`px-3 py-1.5 rounded text-sm capitalize transition-colors ${
                  activeView === v
                    ? 'bg-rs-green text-black font-medium'
                    : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
                }`}
              >
                {v === 'chart' ? 'Chart' : 'Map'}
              </button>
            ))}
          </div>
        </>
      ) : (
        <div className="flex items-center gap-2 flex-wrap">
          {(Object.keys(TIME_PERIOD_LABELS) as TimePeriod[]).map(tp => (
            <button
              key={tp}
              onClick={() => setTimePeriod(tp)}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                timePeriod === tp
                  ? 'bg-rs-green text-black font-medium'
                  : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
            >
              {TIME_PERIOD_LABELS[tp]}
            </button>
          ))}

          <div className="flex-1" />

          {(['kgs', 'lbs'] as Unit[]).map(u => (
            <button
              key={u}
              onClick={() => setUnit(u)}
              className={`px-3 py-1.5 rounded text-sm transition-colors ${
                unit === u
                  ? 'bg-rs-green text-black font-medium'
                  : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
            >
              {u}
            </button>
          ))}

          {(['chart', 'map'] as const).map(v => (
            <button
              key={v}
              onClick={() => setActiveView(v)}
              className={`px-3 py-1.5 rounded text-sm capitalize transition-colors ${
                activeView === v
                  ? 'bg-rs-green text-black font-medium'
                  : 'bg-rs-jet border border-rs-jet-light text-rs-off-white/70 hover:bg-rs-jet-light'
              }`}
            >
              {v === 'chart' ? 'Chart' : 'Map'}
            </button>
          ))}
        </div>
      )}

      <div className={`flex-1 min-h-0 border border-rs-jet-light rounded-lg ${isCompact ? 'p-1' : 'p-4'} flex flex-col ${isDark ? 'bg-rs-off-black/40' : 'bg-rs-off-black'}`}>
        <h3 className={`text-rs-off-white font-medium text-sm mb-3 ${isCompact ? 'px-1' : ''}`}>{chartTitle}</h3>
        {activeView === 'chart' ? (
          <>
            {loading ? (
              <div className="flex-1 flex items-center justify-center text-rs-off-white/40 text-sm">Loading…</div>
            ) : (
              <div className="h-[700px]">
                <Line data={chartData} options={chartOptions as any} plugins={[hoverLinePlugin]} />
              </div>
            )}
            {!loading && hasWindowing && (
              <ChartRangeSlider
                totalPoints={chartLabels.length}
                startIndex={startIndex}
                endIndex={endIndex}
                onChange={setWindow}
              />
            )}
          </>
        ) : (
          <div className="flex-1 min-h-0">
            <UsitcWorldMap
              data={filtered}
              selectedCountries={selectedCountries}
              selectedRegions={selectedRegions}
              unit={unit}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default UsitcChart;
